/**
 * greetingPolicy — pure decision for whether the one-shot idle
 * greeting should be emitted into the chat as an ``agent_note``
 * event. Phase 9.5 Bundle B+D follow-up.
 *
 * Extracted so the shell's useEffect stays a one-liner and the
 * decision branches are testable without mounting VariantCShell
 * (the shell is too tangled with Office.js / msw to cover in
 * jsdom).
 *
 * SRP: pure function; no React, no I/O, no side effects.
 *
 * # DECISION: the policy uses a callsite-supplied "already emitted
 * #   this session" flag rather than peeking at React refs.
 * # WHY: the ref gate prevents double-emit on a re-render burst,
 * #      but the ref itself is React state — keeping it out of the
 * #      pure helper means the helper has no React surface.
 * #      Caller's useEffect owns the single-fire ref; the helper
 * #      owns the rules.
 * # DATE: 2026-04-27
 */

import type { EventLogEntry } from "./eventLog";

export interface GreetingPolicyInput {
  /** Has this React mount already emitted a greeting? Caller's
   *  useRef. False on the first call per mount. */
  alreadyEmittedThisSession: boolean;
  /** Document classification has resolved (Office read finished
   *  OR the no-Office fallback fired). When false, we wait so the
   *  greeting picks the correct doctype-aware copy. */
  classificationReady: boolean;
  /** The event log entries — scanned for a prior greeting from a
   *  past session. Persisted via chatHistoryStore. */
  log: readonly EventLogEntry[];
  /** Sentinel ``commandId`` the caller stamps on the greeting
   *  event. The policy uses it to detect prior emissions. */
  greetingSentinelId: string;
}

/** Three-way decision so the caller can distinguish "skip and don't
 *  bother again" from "skip for now, retry when state changes".
 *
 *   * ``"emit"`` — write the greeting now.
 *   * ``"skip-and-mark"`` — caller should set its single-fire ref
 *     true (a prior greeting exists OR we'd never emit again).
 *   * ``"wait"`` — skip THIS render; classification isn't ready.
 *     Don't mark the ref — the next render may flip the gate.
 */
export type GreetingDecision = "emit" | "skip-and-mark" | "wait";

/**
 * The full decision table. Pure — same input → same output.
 *
 * Order matters: single-fire short-circuit first (cheapest), then
 * classification gate (the only "wait" path), then prior-greeting
 * scan (handles the reload case).
 */
export function decideGreetingEmission(input: GreetingPolicyInput): GreetingDecision {
  if (input.alreadyEmittedThisSession) return "skip-and-mark";
  if (!input.classificationReady) return "wait";
  if (hasPriorGreeting(input.log, input.greetingSentinelId)) {
    return "skip-and-mark";
  }
  return "emit";
}

function hasPriorGreeting(log: readonly EventLogEntry[], sentinelId: string): boolean {
  for (const e of log) {
    if (e.payload.kind === "agent_note" && e.payload.commandId === sentinelId) {
      return true;
    }
  }
  return false;
}
